import {
  useUpdateUserEmploymentByIdMutation,
  useUserEmploymentByIdQuery,
} from "@codegen/index";
import { UpdateUserEmploymentByIdInput, UserEmployment } from "@codegen/schema";
import { Alert } from "@components/Alert";
import {
  FormContainer,
  OnboardingContainer,
  OnboardingFormField,
} from "@components/onboarding";
import { useReadOnlyMode } from "@components/readOnlyModeProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { UnexpectedErrorPage } from "@pages/onboarding/shared/unexpected-error";
import { OnboardingPageType } from "@pages/onboarding/types";
import { useDebounceCallback } from "@react-hook/debounce";
import { useLogError } from "@utils/error";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { FormLoading } from "./loading";

const employmentHistorySchema = z
  .object({
    employerName: z.string().min(1, "Please confirm your employer name"),
    startDate: z.string().refine((val) => !isNaN(Date.parse(val)), {
      message: "Please provide a valid start date",
    }),
    lastDate: z.string().optional(),
    isCurrentEmployment: z.boolean(),
    city: z.string().optional(),
    country: z.string().optional(),
  })
  .refine(
    ({ startDate, lastDate, isCurrentEmployment }) => {
      if (!isCurrentEmployment && lastDate) {
        return new Date(lastDate) > new Date(startDate);
      } else {
        return true;
      }
    },
    {
      path: ["lastDate"],
      message: "End date must be after start date",
    }
  );

type EmploymentHistoryValues = z.infer<typeof employmentHistorySchema>;

export const EmploymentHistoryForm: React.FC<
  OnboardingPageType & {
    employment: Pick<
      UserEmployment,
      | "companyName"
      | "id"
      | "startDate"
      | "endDate"
      | "isCurrent"
      | "city"
      | "country"
    >;
    refetch?: () => void;
  }
> = ({
  onSubmit,
  autoSave,
  onBack,
  onSkipForNow,
  employment,
  exhibit,
  moduleProgress,
  currentStep,
  showSkipForNow,
  hideExitButton,
  refetch,
}) => {
  const [updateUserEmployment] = useUpdateUserEmploymentByIdMutation();
  const [status, setStatus] = useState<
    "idle" | "loading" | "error" | "success"
  >("idle");
  const logError = useLogError();
  const { isReadOnly } = useReadOnlyMode();

  // Track pending save operations to avoid race conditions
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  const methods = useForm({
    defaultValues: {
      employerName: employment?.companyName || "",
      startDate: employment?.startDate || "",
      lastDate: employment?.endDate || "",
      isCurrentEmployment: employment?.isCurrent || false,
      city: employment?.city || "",
      country: employment?.country || "",
    },
    resolver: zodResolver(employmentHistorySchema),
    mode: "onChange",
  });

  const { control, watch, setError } = methods;
  const watchedValues = useWatch({ control });
  const isCurrentEmployment = watch("isCurrentEmployment");

  const performMutation = useCallback(
    async (data: EmploymentHistoryValues) => {
      const { errors } = await updateUserEmployment({
        variables: {
          input: {
            clientMutationId: "",
            id: employment.id,
            userEmploymentPatch: {
              companyName: data.employerName,
              startDate: data.startDate,
              endDate:
                data.lastDate && !data.isCurrentEmployment
                  ? data.lastDate
                  : null,
              isCurrent: data.isCurrentEmployment,
              city: data.city,
              country: data.country,
            } as UpdateUserEmploymentByIdInput["userEmploymentPatch"],
          },
        },
      });

      if (errors != null) {
        throw errors[0];
      }

      // Call refetch if available to update the query cache
      if (refetch) {
        await refetch();
      }
    },
    [updateUserEmployment, employment.id, refetch]
  );

  const handleSubmit = useCallback(
    async (data: EmploymentHistoryValues) => {
      try {
        // Wait for any pending auto-save to complete
        if (pendingSaveRef.current) {
          await pendingSaveRef.current;
        }

        setStatus("loading");
        await performMutation(data);
        setStatus("success");
        onSubmit(data);
      } catch (exception) {
        setStatus("error");
        if (exception instanceof Error) {
          setError("root.serverError", {
            type: exception.name,
            message: exception.message,
          });
        }
        logError(exception, {
          message: "Error submitting employment history",
          employment,
        });
      }
    },
    [performMutation, onSubmit, setError, logError, employment]
  );

  const handleSave = useCallback(
    async (data: EmploymentHistoryValues) => {
      const savePromise = (async () => {
        try {
          setStatus("loading");
          await performMutation(data);
          setStatus("success");
          autoSave(data);
        } catch (exception) {
          setStatus("error");
          if (exception instanceof Error) {
            setError("root.serverError", {
              type: exception.name,
              message: exception.message,
            });
          }
          logError(exception, {
            message: "Error saving employment history",
            employment,
          });
        } finally {
          pendingSaveRef.current = null;
        }
      })();

      pendingSaveRef.current = savePromise;
      await savePromise;
    },
    [performMutation, autoSave, setError, logError, employment]
  );

  const handleSkipForNow = useCallback(async () => {
    try {
      if (watchedValues) {
        await onSkipForNow?.(watchedValues);
      } else {
        onSkipForNow?.();
      }
    } catch (e) {
      logError(e, { message: "Error skipping" });
    }
  }, [watchedValues, onSkipForNow, logError]);

  const debouncedSave = useDebounceCallback(handleSave, 300);

  useEffect(() => {
    // Only call debouncedSave if the form data is valid
    const parseResult = employmentHistorySchema.safeParse(watchedValues);
    if (parseResult.success) {
      debouncedSave(parseResult.data);
    }
  }, [watchedValues, debouncedSave]);

  return (
    <FormProvider {...methods}>
      <OnboardingContainer
        title="Professional Details"
        subtitle={exhibit?.name}
        description={exhibit?.description}
        onSubmit={
          methods.handleSubmit(handleSubmit) as (
            data?: unknown
          ) => void | Promise<void>
        }
        onBack={onBack}
        onSkipForNow={handleSkipForNow}
        name="employment_history"
        isLoading={status === "loading"}
        progress={moduleProgress}
        currentStep={currentStep}
        showSkipForNow={showSkipForNow}
        hideExitButton={hideExitButton}
      >
        <FormContainer
          errorMessage="There was a problem saving your employment details. Please try again."
          successMessage={
            status === "success"
              ? "Employment details saved successfully."
              : undefined
          }
        >
          <Alert variant="tip" hideIcon className="mt-4 mb-4">
            Please include the date in which you first started here, including
            contract or part-time work. (e.g. From September 2023 through July
            2025 or from April 2025 through present) If you have not started
            yet, please leave this section blank and our team will input a
            prospective start date for you.
          </Alert>
          <OnboardingFormField
            name="employerName"
            label="Company legal name"
            type="text"
            required
            placeholder="Google Inc."
            disabled={isReadOnly}
          />

          <div className="flex justify-between gap-3">
            <OnboardingFormField
              name="city"
              label="City"
              type="text"
              placeholder="New York"
              disabled={isReadOnly}
              className="w-1/2"
            />

            <OnboardingFormField
              name="country"
              label="Country"
              type="text"
              placeholder="US"
              disabled={isReadOnly}
              className="w-1/2"
            />
          </div>

          <div className="flex justify-between gap-3">
            <OnboardingFormField
              name="startDate"
              label="Start date"
              type="text"
              required
              placeholder="YYYY-MM-DD"
              disabled={isReadOnly}
              className="w-1/2"
              style={{ WebkitAppearance: "none" }}
            />

            <div className="w-1/2">
              <OnboardingFormField
                name="lastDate"
                label="Last date"
                type="text"
                placeholder="YYYY-MM-DD"
                disabled={isCurrentEmployment || isReadOnly}
                style={{ WebkitAppearance: "none" }}
              />

              <OnboardingFormField
                name="isCurrentEmployment"
                type="checkbox"
                label="I currently work here"
                disabled={isReadOnly}
                className="mt-2"
              />
            </div>
          </div>
        </FormContainer>
      </OnboardingContainer>
    </FormProvider>
  );
};

export const EmploymentHistoryFormController: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, onSubmit, module } = props;
  // Use generic interfaces to avoid missing enum imports
  const { company } = exhibit as { company?: { companyId?: string } };
  const { employments } = module as {
    employments?: Array<{ companyId?: string; employmentId?: number }>;
  };

  const employmentObj = employments?.find(
    (e) => e.companyId === company?.companyId
  );

  const {
    data: employmentData,
    error,
    refetch,
    loading,
  } = useUserEmploymentByIdQuery({
    variables: {
      id: employmentObj?.employmentId || 0,
    },
    skip: !employmentObj?.employmentId,
    // Add fetchPolicy to ensure fresh data on navigation
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  const logError = useLogError();

  useEffect(() => {
    if (error && employmentObj) {
      logError(
        `Unable to find user employment ID: ${employmentObj.employmentId}`,
        {
          employmentObj,
          error,
        }
      );
    }
  }, [error, employmentObj, logError]);

  const handleSubmit = useCallback(
    async (data: EmploymentHistoryValues) => {
      await onSubmit(data);
      refetch();
    },
    [onSubmit, refetch]
  );

  // Don't show error state for missing employmentId - just show loading
  if (!employmentObj?.employmentId) {
    return <FormLoading />;
  }

  if (error || (!loading && !employmentData?.userEmploymentById)) {
    return <UnexpectedErrorPage {...props} error={error} />;
  }

  if (employmentData && employmentData.userEmploymentById) {
    return (
      <EmploymentHistoryForm
        {...props}
        onSubmit={handleSubmit}
        employment={employmentData.userEmploymentById}
        refetch={refetch}
      />
    );
  }

  return <FormLoading />;
};

export default EmploymentHistoryFormController;
